import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar
} from '@mui/material';
import reservationService from '../services/reservationService';
import { useLanguage } from '../App';

// Mock data for properties and sites - replace with actual API calls
const mockProperties = [
  { id: '1', nickname: 'NR305', owner: 'Allan Homenhuck' },
  { id: '2', nickname: 'Beach House', owner: 'Maria Silva' },
  { id: '3', nickname: 'City Apartment', owner: 'João Santos' }
];

const mockSites = [
  { id: '1', name: 'Airbnb', color: '#FF5A5F' },
  { id: '2', name: 'Booking.com', color: '#003580' },
  { id: '3', name: 'VRBO', color: '#0066CC' },
  { id: '4', name: 'Expedia', color: '#FFC72C' },
  { id: '5', name: 'Direct', color: '#4CAF50' }
];

// Form translations
const formTranslations = {
  en: {
    new_reservation: 'New Reservation',
    edit_reservation: 'Edit Reservation',
    confirmation_code: 'Confirmation Code',
    guest_first_name: 'Guest First Name',
    guest_last_name: 'Guest Last Name',
    guest_phone: 'Guest Phone',
    check_in_date: 'Check-in Date',
    check_out_date: 'Check-out Date',
    property: 'Property',
    select_property: 'Select Property',
    number_of_guests: 'Number of Guests',
    number_of_nights: 'Number of Nights',
    booking_channel: 'Booking Channel',
    select_channel: 'Select Channel',
    total_payout: 'Total Host Payout',
    status: 'Status',
    save: 'Save',
    cancel: 'Cancel',
    loading: 'Loading...',
    saving: 'Saving...',
    reservation_saved: 'Reservation saved successfully!',
    error_saving: 'Error saving reservation',
    error_loading: 'Error loading reservation'
  },
  pt: {
    new_reservation: 'Nova Reserva',
    edit_reservation: 'Editar Reserva',
    confirmation_code: 'Código de Confirmação',
    guest_first_name: 'Nome do Hóspede',
    guest_last_name: 'Sobrenome do Hóspede',
    guest_phone: 'Telefone do Hóspede',
    check_in_date: 'Data de Check-in',
    check_out_date: 'Data de Check-out',
    property: 'Propriedade',
    select_property: 'Selecionar Propriedade',
    number_of_guests: 'Número de Hóspedes',
    number_of_nights: 'Número de Noites',
    booking_channel: 'Canal de Reserva',
    select_channel: 'Selecionar Canal',
    total_payout: 'Pagamento Total do Host',
    status: 'Status',
    save: 'Salvar',
    cancel: 'Cancelar',
    loading: 'Carregando...',
    saving: 'Salvando...',
    reservation_saved: 'Reserva salva com sucesso!',
    error_saving: 'Erro ao salvar reserva',
    error_loading: 'Erro ao carregar reserva'
  },
  es: {
    new_reservation: 'Nueva Reserva',
    edit_reservation: 'Editar Reserva',
    confirmation_code: 'Código de Confirmación',
    guest_first_name: 'Nombre del Huésped',
    guest_last_name: 'Apellido del Huésped',
    guest_phone: 'Teléfono del Huésped',
    check_in_date: 'Fecha de Check-in',
    check_out_date: 'Fecha de Check-out',
    property: 'Propiedad',
    select_property: 'Seleccionar Propiedad',
    number_of_guests: 'Número de Huéspedes',
    number_of_nights: 'Número de Noches',
    booking_channel: 'Canal de Reserva',
    select_channel: 'Seleccionar Canal',
    total_payout: 'Pago Total del Host',
    status: 'Estado',
    save: 'Guardar',
    cancel: 'Cancelar',
    loading: 'Cargando...',
    saving: 'Guardando...',
    reservation_saved: '¡Reserva guardada exitosamente!',
    error_saving: 'Error al guardar reserva',
    error_loading: 'Error al cargar reserva'
  }
};

function ReservationForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isEdit = Boolean(id);
  
  const t = (key) => {
    return formTranslations[language]?.[key] || formTranslations.en[key] || key;
  };
  
  const [formData, setFormData] = useState({
    confirmationCode: '',
    guestFirstName: '',
    guestLastName: '',
    guestPhone: '',
    checkInDate: new Date().toISOString().split('T')[0], // Pre-fill with today's date
    checkOutDate: new Date(Date.now() + 24*60*60*1000).toISOString().split('T')[0], // Tomorrow
    listingNickname: '',
    numberOfGuests: '',
    numberOfNights: '',
    channel: '',
    totalHostPayout: '',
    status: 'Booking confirmed'
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [properties, setProperties] = useState([]);
  const [sites, setSites] = useState([]);
  const [availableChannels, setAvailableChannels] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    loadPropertiesAndSites();
  }, []);

  useEffect(() => {
    if (isEdit) {
      loadReservation();
    }
  }, [id, isEdit]);

  const loadPropertiesAndSites = async () => {
    try {
      // Load properties from localStorage (same as PropertyList)
      const savedProperties = localStorage.getItem('properties');
      if (savedProperties) {
        setProperties(JSON.parse(savedProperties));
      }

      // Load sites from localStorage (same as SitesList)
      const savedSites = localStorage.getItem('sites');
      if (savedSites) {
        setSites(JSON.parse(savedSites));
      }
    } catch (err) {
      console.error('Error loading properties and sites:', err);
    }
  };

  const loadReservation = async () => {
    try {
      setLoading(true);
      const reservation = await reservationService.getReservation(id);
      setFormData(reservation);
    } catch (err) {
      setSnackbar({ open: true, message: `${t('error_loading')}: ${err.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value
    });

    // When property is selected, update available channels
    if (name === 'listingNickname') {
      const selectedProperty = properties.find(p => p.name === value);
      if (selectedProperty) {
        // Get channels that match the property's links
        const propertyChannels = [];
        
        // Check each site to see if the property has a link for it
        sites.forEach(site => {
          const linkKey = site.name.toLowerCase().replace(/\s+/g, '');
          if (selectedProperty.links && selectedProperty.links[linkKey]) {
            propertyChannels.push(site.name);
          }
        });
        
        setAvailableChannels(propertyChannels);
        
        // Clear channel selection if current channel is not available for this property
        if (formData.channel && !propertyChannels.includes(formData.channel)) {
          setFormData(prev => ({ ...prev, channel: '' }));
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      const validation = reservationService.validateReservation(formData, language);
      if (!validation.isValid) {
        setSnackbar({ open: true, message: 'Por favor, corrija os erros de validação', severity: 'warning' });
        return;
      }

      if (isEdit) {
        await reservationService.updateReservation(id, formData);
      } else {
        await reservationService.createReservation(formData);
      }
      
      setSnackbar({ open: true, message: t('reservation_saved'), severity: 'success' });
      setTimeout(() => navigate('/reservations'), 1500);
    } catch (err) {
      setSnackbar({ open: true, message: `${t('error_saving')}: ${err.message}`, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? t('edit_reservation') : t('new_reservation')}
      </Typography>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('confirmation_code')}
                name="confirmationCode"
                value={formData.confirmationCode}
                onChange={handleChange}
                required
                disabled={isEdit}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Status"
                name="status"
                value={formData.status}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Guest First Name"
                name="guestFirstName"
                value={formData.guestFirstName}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Guest Last Name"
                name="guestLastName"
                value={formData.guestLastName}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Guest Phone"
                name="guestPhone"
                value={formData.guestPhone}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t('property_name')}</InputLabel>
                <Select
                  name="listingNickname"
                  value={formData.listingNickname}
                  onChange={handleChange}
                  label={t('property_name')}
                >
                  {properties.map((property) => (
                    <MenuItem key={property.id} value={property.name}>
                      {property.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Check-in Date"
                name="checkInDate"
                type="date"
                value={formData.checkInDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Check-out Date"
                name="checkOutDate"
                type="date"
                value={formData.checkOutDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Number of Guests"
                name="numberOfGuests"
                type="number"
                value={formData.numberOfGuests}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Number of Nights"
                name="numberOfNights"
                type="number"
                value={formData.numberOfNights}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>{t('booking_channel')}</InputLabel>
                <Select
                  name="channel"
                  value={formData.channel}
                  onChange={handleChange}
                  label={t('booking_channel')}
                  disabled={!formData.listingNickname}
                >
                  {availableChannels.map((channel) => (
                    <MenuItem key={channel} value={channel}>
                      {channel}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Total Host Payout"
                name="totalHostPayout"
                type="number"
                value={formData.totalHostPayout}
                onChange={handleChange}
                InputProps={{ startAdornment: '$' }}
              />
            </Grid>
          </Grid>

          <Box mt={3} display="flex" gap={2}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : (isEdit ? 'Update' : 'Create')} Reservation
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/reservations')}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ReservationForm;
